/**
 * Wizard Container Component
 *
 * Manages multi-step wizard flows with navigation, validation, and progress tracking.
 * Supports saving draft state and continuing later.
 */

import React, { useState, useCallback, type ReactNode } from 'react';

export interface WizardStep {
  id: string;
  label: string;
  description?: string;
  validate?: () => boolean | Promise<boolean>;
  optional?: boolean;
}

export interface WizardProps {
  steps: WizardStep[];
  currentStep?: number;
  onStepChange?: (stepIndex: number) => void;
  onComplete?: (data?: any) => void | Promise<void>;
  onSaveDraft?: (data?: any) => void | Promise<void>;
  className?: string;
  children?: ReactNode;
}

export interface WizardContextValue {
  currentStep: number;
  totalSteps: number;
  steps: WizardStep[];
  isFirstStep: boolean;
  isLastStep: boolean;
  canGoNext: boolean;
  canGoBack: boolean;
  goToStep: (stepIndex: number) => void;
  goNext: () => Promise<void>;
  goBack: () => void;
  complete: () => Promise<void>;
  saveDraft: () => Promise<void>;
  setStepValid: (valid: boolean) => void;
}

export const WizardContext = React.createContext<WizardContextValue | undefined>(undefined);

export const useWizard = () => {
  const context = React.useContext(WizardContext);
  if (!context) {
    throw new Error('useWizard must be used within a Wizard component');
  }
  return context;
};

export const Wizard: React.FC<WizardProps> = ({
  steps,
  currentStep: controlledStep,
  onStepChange,
  onComplete,
  onSaveDraft,
  className = '',
  children,
}) => {
  const [internalStep, setInternalStep] = useState(0);
  const [isStepValid, setIsStepValid] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  const currentStep = controlledStep ?? internalStep;
  const isControlled = controlledStep !== undefined;

  const updateStep = useCallback((newStep: number) => {
    if (isControlled) {
      onStepChange?.(newStep);
    } else {
      setInternalStep(newStep);
    }
  }, [isControlled, onStepChange]);

  const totalSteps = steps.length;
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;

  const goToStep = useCallback((stepIndex: number) => {
    if (stepIndex >= 0 && stepIndex < totalSteps) {
      updateStep(stepIndex);
    }
  }, [totalSteps, updateStep]);

  const goNext = useCallback(async () => {
    if (isLastStep || isProcessing) return;

    setIsProcessing(true);
    try {
      const currentStepData = steps[currentStep];
      if (currentStepData.validate) {
        const isValid = await currentStepData.validate();
        if (!isValid) {
          setIsProcessing(false);
          return;
        }
      }
      updateStep(currentStep + 1);
    } finally {
      setIsProcessing(false);
    }
  }, [currentStep, isLastStep, steps, updateStep, isProcessing]);

  const goBack = useCallback(() => {
    if (!isFirstStep && !isProcessing) {
      updateStep(currentStep - 1);
    }
  }, [currentStep, isFirstStep, updateStep, isProcessing]);

  const complete = useCallback(async () => {
    if (!isLastStep || isProcessing) return;

    setIsProcessing(true);
    try {
      const currentStepData = steps[currentStep];
      if (currentStepData.validate) {
        const isValid = await currentStepData.validate();
        if (!isValid) {
          setIsProcessing(false);
          return;
        }
      }
      await onComplete?.();
    } finally {
      setIsProcessing(false);
    }
  }, [currentStep, isLastStep, steps, onComplete, isProcessing]);

  const saveDraft = useCallback(async () => {
    if (isProcessing) return;

    setIsProcessing(true);
    try {
      await onSaveDraft?.();
    } finally {
      setIsProcessing(false);
    }
  }, [onSaveDraft, isProcessing]);

  const contextValue: WizardContextValue = {
    currentStep,
    totalSteps,
    steps,
    isFirstStep,
    isLastStep,
    canGoNext: !isLastStep && isStepValid && !isProcessing,
    canGoBack: !isFirstStep && !isProcessing,
    goToStep,
    goNext,
    goBack,
    complete,
    saveDraft,
    setStepValid: setIsStepValid,
  };

  // Filter children to only show current step
  const childrenArray = React.Children.toArray(children);
  const currentChildren = childrenArray.map((child, index) => {
    if (index === currentStep) {
      return child;
    }
    return null;
  });

  return (
    <WizardContext.Provider value={contextValue}>
      <div className={`wizard ${className}`}>
        {currentChildren}
      </div>
    </WizardContext.Provider>
  );
};
